import React, { useState, useEffect } from 'react';
import useXHR from '../../hooks/useXHR';
import { API } from '../../utils/api';
/**
 * NotificationPreferences - Manage notification settings
 * Allows users to control which notifications they receive
 */
const NotificationPreferences = ({ token, showToast }) => {
  const { get, post, del: xhrDel, put } = useXHR(token);
  const [preferences, setPreferences] = useState({
    email_notifications: true,
    push_notifications: true,
    share_notifications: true,
    friend_notifications: true,
    category_notifications: true,
    achievement_notifications: true,
    newsletter_notifications: true,
    quiet_hours_enabled: false,
    quiet_hours_start: 22,
    quiet_hours_end: 8
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [summary, setSummary] = useState(null);

  // Fetch current preferences
  useEffect(() => {
    const fetchPreferences = async () => {
      if (!token) {
        setLoading(false);
        return;
      }
      
      try {
        const [prefsData, summaryData] = await Promise.all([
          get('/notifications/preferences'),
          get('/notifications/summary')
        ]);
        setPreferences(prefsData);
        setSummary(summaryData);
      } catch (e) {
        console.error('Failed to fetch preferences:', e);
      }
      setLoading(false);
    };
    
    fetchPreferences();
  }, [get, token]);

  // Save preferences
  const savePreferences = async () => {
    setSaving(true);
    try {
      await put('/notifications/preferences', { body: preferences });
      showToast && showToast('Preferences saved!', 'success');
    } catch (e) {
      showToast && showToast('Error saving preferences', 'error');
    }
    setSaving(false);
  };

  // Toggle a boolean preference
  const togglePreference = (key) => {
    setPreferences(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const preferenceItems = [
    { key: 'email_notifications', label: 'Email Notifications', icon: '📧', description: 'Receive email alerts for important updates' },
    { key: 'push_notifications', label: 'Push Notifications', icon: '🔔', description: 'Browser push notifications when you\'re offline' },
    { key: 'share_notifications', label: 'Share Alerts', icon: '🔗', description: 'When someone shares a category with you' },
    { key: 'friend_notifications', label: 'Friend Activity', icon: '👥', description: 'Friend requests and activity updates' },
    { key: 'category_notifications', label: 'Category Updates', icon: '📁', description: 'New results in your categories' },
    { key: 'achievement_notifications', label: 'Achievements', icon: '🏆', description: 'Gamification rewards and milestones' },
    { key: 'newsletter_notifications', label: 'Newsletter', icon: '📰', description: 'Weekly digest and platform news' }
  ];

  if (loading) {
    return (
      <div style={{
        padding: 40,
        textAlign: 'center',
        color: '#a1a1aa'
      }}>
        Loading preferences...
      </div>
    );
  }

  return (
    <div data-testid="notification-preferences" style={{
      background: 'rgba(30, 30, 35, 0.9)',
      border: '1px solid rgba(255,255,255,0.1)',
      borderRadius: 16,
      padding: 24
    }}>
      <h3 style={{ 
        margin: '0 0 8px 0', 
        color: '#0F172A',
        fontSize: '1.1rem',
        display: 'flex',
        alignItems: 'center',
        gap: 10
      }}>
        🔔 Notification Preferences
      </h3>
      <p style={{ 
        color: '#a1a1aa', 
        fontSize: '0.85rem', 
        marginBottom: 20,
        lineHeight: 1.5
      }}>
        Control how and when you receive notifications
      </p>

      {/* Summary stats */}
      {summary && (
        <div style={{
          display: 'flex',
          gap: 15,
          marginBottom: 20,
          padding: 15,
          background: 'rgba(253, 224, 71, 0.1)',
          borderRadius: 10,
          flexWrap: 'wrap'
        }}>
          <div style={{ textAlign: 'center', flex: 1, minWidth: 70 }}>
            <div style={{ color: '#FDE047', fontSize: '1.5rem', fontWeight: 700 }}>
              {summary.total}
            </div>
            <div style={{ color: '#a1a1aa', fontSize: '0.75rem' }}>Total</div>
          </div>
          <div style={{ textAlign: 'center', flex: 1, minWidth: 70 }}>
            <div style={{ color: '#ef4444', fontSize: '1.5rem', fontWeight: 700 }}>
              {summary.unread}
            </div>
            <div style={{ color: '#a1a1aa', fontSize: '0.75rem' }}>Unread</div>
          </div>
          <div style={{ textAlign: 'center', flex: 1, minWidth: 70 }}>
            <div style={{ color: '#10b981', fontSize: '1.5rem', fontWeight: 700 }}>
              {summary.total - summary.unread}
            </div>
            <div style={{ color: '#a1a1aa', fontSize: '0.75rem' }}>Read</div>
          </div>
        </div>
      )}

      {/* Preference toggles */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {preferenceItems.map(item => (
          <div 
            key={item.key}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '12px 15px',
              background: preferences[item.key] 
                ? 'rgba(253, 224, 71, 0.1)' 
                : 'rgba(255, 255, 255, 0.85)',
              borderRadius: 10,
              border: `1px solid ${preferences[item.key] ? 'rgba(253, 224, 71, 0.3)' : 'transparent'}`,
              transition: 'all 0.2s'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ fontSize: '1.2rem' }}>{item.icon}</span>
              <div>
                <div style={{ color: '#e5e5e5', fontWeight: 500, fontSize: '0.9rem' }}>
                  {item.label}
                </div>
                <div style={{ color: '#6b7280', fontSize: '0.75rem' }}>
                  {item.description}
                </div>
              </div>
            </div>
            <button
              onClick={() => togglePreference(item.key)}
              style={{
                width: 50,
                height: 28,
                borderRadius: 14,
                background: preferences[item.key] 
                  ? 'linear-gradient(135deg, #FDE047, #FCD34D)' 
                  : 'rgba(255,255,255,0.1)',
                border: 'none',
                cursor: 'pointer',
                position: 'relative',
                transition: 'all 0.2s'
              }}
              data-testid={`toggle-${item.key}`}
            >
              <div style={{
                width: 22,
                height: 22,
                borderRadius: '50%',
                background: '#fff',
                position: 'absolute',
                top: 3,
                left: preferences[item.key] ? 25 : 3,
                transition: 'left 0.2s',
                boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
              }} />
            </button>
          </div>
        ))}
      </div>

      {/* Quiet Hours */}
      <div style={{
        marginTop: 20,
        padding: 15,
        background: 'rgba(245, 158, 11, 0.1)',
        borderRadius: 10
      }}>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          marginBottom: preferences.quiet_hours_enabled ? 15 : 0
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: '1.2rem' }}>🌙</span>
            <div>
              <div style={{ color: '#e5e5e5', fontWeight: 500, fontSize: '0.9rem' }}>
                Quiet Hours
              </div>
              <div style={{ color: '#6b7280', fontSize: '0.75rem' }}>
                Pause notifications during specific hours
              </div>
            </div>
          </div>
          <button
            onClick={() => togglePreference('quiet_hours_enabled')}
            style={{
              width: 50,
              height: 28,
              borderRadius: 14,
              background: preferences.quiet_hours_enabled 
                ? 'linear-gradient(135deg, #3b82f6, #FCD34D)' 
                : 'rgba(255,255,255,0.1)',
              border: 'none',
              cursor: 'pointer',
              position: 'relative',
              transition: 'all 0.2s'
            }}
            data-testid="toggle-quiet-hours"
          >
            <div style={{
              width: 22,
              height: 22,
              borderRadius: '50%',
              background: '#fff',
              position: 'absolute',
              top: 3,
              left: preferences.quiet_hours_enabled ? 25 : 3,
              transition: 'left 0.2s',
              boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
            }} />
          </button>
        </div>

        {preferences.quiet_hours_enabled && (
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 15,
            flexWrap: 'wrap'
          }}>
            <div style={{ flex: 1, minWidth: 120 }}>
              <label style={{ display: 'block', color: '#a1a1aa', fontSize: '0.8rem', marginBottom: 5 }}>
                From
              </label>
              <select
                value={preferences.quiet_hours_start}
                onChange={(e) => setPreferences(prev => ({ 
                  ...prev, 
                  quiet_hours_start: parseInt(e.target.value) 
                }))}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  background: 'rgba(255, 255, 255, 0.85)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 8,
                  color: '#0F172A',
                  fontSize: '0.9rem'
                }}
                data-testid="quiet-hours-start"
              >
                {Array.from({ length: 24 }, (_, i) => (
                  <option key={i} value={i}>
                    {i.toString().padStart(2, '0')}:00
                  </option>
                ))}
              </select>
            </div>
            <div style={{ flex: 1, minWidth: 120 }}>
              <label style={{ display: 'block', color: '#a1a1aa', fontSize: '0.8rem', marginBottom: 5 }}>
                To
              </label>
              <select
                value={preferences.quiet_hours_end}
                onChange={(e) => setPreferences(prev => ({ 
                  ...prev, 
                  quiet_hours_end: parseInt(e.target.value) 
                }))}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  background: 'rgba(255, 255, 255, 0.85)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: 8,
                  color: '#0F172A',
                  fontSize: '0.9rem'
                }}
                data-testid="quiet-hours-end"
              >
                {Array.from({ length: 24 }, (_, i) => (
                  <option key={i} value={i}>
                    {i.toString().padStart(2, '0')}:00
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}
      </div>

      {/* Save button */}
      <button
        onClick={savePreferences}
        disabled={saving}
        style={{
          width: '100%',
          padding: '12px',
          marginTop: 20,
          background: 'linear-gradient(135deg, #FDE047, #FCD34D)',
          border: 'none',
          borderRadius: 10,
          color: '#0F172A',
          fontWeight: 600,
          fontSize: '0.95rem',
          cursor: 'pointer',
          opacity: saving ? 0.7 : 1,
          transition: 'opacity 0.2s'
        }}
        data-testid="save-notification-preferences"
      >
        {saving ? 'Saving...' : 'Save Preferences'}
      </button>
    </div>
  );
};

export default NotificationPreferences;
